/* eslint prefer-spread: 0 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import {
  Grid,
  Table,
  Progress,
  Segment,
  Loader,
  Dimmer,
} from 'semantic-ui-react';
import CountUp from 'react-countup';
import qs from 'qs';

import { decodeSearch } from '../../utils/url';
import { calcConversionAmount, getCompanyBaseCurrency, getDisplayCurrency, getDisplayCurrencySymbol } from '../../utils/currencies';
import config from '../../../config';
import { fetchM } from '../../actions/fetchM';
import { flashErrorMessage } from '../../actions/flash';
import LoanAmount from '../../components/LoanAmount';
import { UNIT_STATUSES_HQ_BASIC } from '../../constants/unitStatuses';
import { namespaces, TRANSLATION_FILES } from '../../constants/translation';
import CurrencySymbol from '../../components/CurrencySymbol';
import OverallUnitReports from './OverallUnitReports';

class ProjectUnitsByStatus extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        currency: PropTypes.shape({
          code: PropTypes.string,
          symbol: PropTypes.string,
        }),
      }),
    }),
    auth: PropTypes.shape({
      company: PropTypes.object,
      isHeadquarters: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
    }),
    barChart: PropTypes.bool,
    headerColor: PropTypes.string,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      query: PropTypes.object,
      search: PropTypes.string,
    }).isRequired,
    projectID: PropTypes.string,
    projectCurrencies: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    t: PropTypes.func,
  }
  static mapStateToProps = state => ({
    appConfig: state.appConfig,
    auth: state.auth,
  });
  static getQueryData = (props) => {
    const query = decodeSearch(props.location.search);
    const projectID = query.projectID || '';
    const projectUnitLayoutID = query.projectUnitLayoutID || '';
    const dateRange = query.dateRange || '';
    const dateFrom = query.dateFrom ? query.dateFrom : '';
    const dateTo = query.dateTo ? query.dateTo : '';
    const businessUnitID = query.businessUnitID ? query.businessUnitID : '';
    return {
      projectID,
      projectUnitLayoutID,
      dateRange,
      dateFrom,
      dateTo,
      businessUnitID,
    };
  }
  constructor(props) {
    super(props);
    const projectCurrencies = props.projectCurrencies;
    this.state = {
      reportData: [],
      loading: true,
      projectCurrencies,
      refetch: true, // whether need to re-call api; fix calendar pick for the from date always call api
    };
  }
  componentDidMount() {
    this.fetchReports();
  }
  componentWillReceiveProps(nextProps) {
    const query = ProjectUnitsByStatus.getQueryData(this.props);
    const newQuery = ProjectUnitsByStatus.getQueryData(nextProps);
    const projectCurrencies = nextProps.projectCurrencies;
    const refetch = newQuery.dateRange === 'custom' ? !!(newQuery.dateFrom && newQuery.dateTo) : true;
    this.setState({
      projectCurrencies,
      refetch,
    });
    if (
      (query.dateRange !== newQuery.dateRange) ||
      (query.dateTo !== newQuery.dateTo) ||
      (query.dateFrom !== newQuery.dateFrom) ||
      (query.projectID !== newQuery.projectID) ||
      (query.businessUnitID !== newQuery.businessUnitID) ||
      (newQuery.projectID !== '' && query.projectUnitLayoutID !== newQuery.projectUnitLayoutID)
    ) {
      if (refetch) {
        this.setState({
          reportData: [],
        }, () => {
          this.fetchReports(nextProps);
        });
      }
    }
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  getConversionAmount(amount) {
    const { projectCurrencies = [] } = this.state;
    const { company } = this.props.auth;
    const companyBaseCurrency = getCompanyBaseCurrency(company);
    const displayCurrency = getDisplayCurrency();
    const companyCurrencyInProject = projectCurrencies.find(e => e.code === companyBaseCurrency.code);
    const displayCurrencyInProject = projectCurrencies.find(e => e.code === displayCurrency);
    const projectBaseCurrency = projectCurrencies.find(e => e.isDefault);
    return calcConversionAmount(
      companyCurrencyInProject,
      displayCurrencyInProject,
      projectBaseCurrency,
      amount,
    );
  }
  setDisplayCurrencySymbol(appConfig) {
    // if there is projectCurrencies, means it is in project level, else is company wide,
    // thus should show base currency symbol instead of display currency symbol
    const { projectCurrencies = [] } = this.state;
    if (projectCurrencies.length > 0) {
      return getDisplayCurrencySymbol();
    }
    return (appConfig && appConfig.data && appConfig.data.currency) ? appConfig.data.currency.symbol : '';
  }
  getValueName = (value, arr) => {
    const { t } = this.props;
    const valueName = arr.find(vn => vn.value === value);
    if (valueName && valueName.text) {
      return t(valueName.text, { ns: TRANSLATION_FILES.constants });
    }
    return (
      <span style={{ textTransform: 'capitalize' }}>
        {t(value)}
      </span>
    );
  }
  getBarStatusColor = (statusID) => {
    switch (statusID) {
      case 'new':
        return 'green';
      case 'reserved':
        return 'yellow';
      case 'booked':
        return 'red';
      case 'sold_spa_signed':
        return 'teal';
      case 'delist':
        return 'grey';
      case 'total':
        return 'blue';
      default:
        return 'black';
    }
  }
  getBarPercent = (data, statusCount) => {
    const total = Math.max.apply(Math, data.map((m) => {
      if (m.statusID !== 'total') {
        return m.count;
      }
      return 0;
    }));
    if (total) {
      return Math.floor((statusCount / total) * 100);
    }
    return 0;
  }
  checkData = (data = []) => {
    const combineData = [];
    const statusMap = [
      { from: 'new', to: 'new', order: 1 },
      { from: 'reserved', to: 'reserved', order: 2 },
      { from: 'developer_reserved', to: 'reserved', order: 2 },
      { from: 'booked', to: 'booked', order: 3 },
      { from: 'developer_booked', to: 'booked', order: 3 },
      { from: 'sold_spa_signed', to: 'sold_spa_signed', order: 4 },
      { from: 'developer_sold', to: 'sold_spa_signed', order: 4 },
      { from: 'delist', to: 'delist', order: 5 },
      { from: 'total', to: 'total', order: 6 },
    ];
    statusMap.forEach((s) => {
      const d = data.find(f => f.statusID === s.from);
      const idx = combineData.findIndex(f => f.statusID === s.to);
      if (idx > -1) {
        combineData[idx].count += d ? d.count : 0;
        combineData[idx].sumSPAValue += d ? d.sumSPAValue : 0;
      } else {
        const ds = {
          statusID: s.to,
          count: d ? d.count : 0,
          sumSPAValue: d ? d.sumSPAValue : 0,
          order: s.order,
        };
        combineData.push(ds);
      }
    });
    return combineData.sort((a, b) => {
      if (a.order < b.order) {
        return -1;
      }
      if (a.order > b.order) {
        return 1;
      }
      return 0;
    });
  }
  async fetchReports(props = this.props) {
    await this.setLoading(true);
    const { store } = this.context;
    const { t } = this.props;
    const q = decodeSearch(props.location.search);
    const query = Object.assign({}, q);
    delete query.projectID;
    delete query.projectUnitLayoutID;
    const projectID = (props.projectID || q.projectID) || '';
    const projectUnitLayoutID = q.projectUnitLayoutID || '';
    if (projectID !== '') {
      query.projectID = projectID;
      if (projectUnitLayoutID !== '') {
        query.projectUnitLayoutID = projectUnitLayoutID;
      }
    }
    let res;
    try {
      res = await store.dispatch(fetchM(`${config.get('bookApiURL')}/reports/project_units/statuses/?${qs.stringify(query)}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
    } catch (e) {
      Bugsnag.notifyException(e);
      await store.dispatch(flashErrorMessage({ message: t('An error occurred, please refresh page', { ns: TRANSLATION_FILES.translation }) }));
      await this.setLoading(false);
      return;
    }
    if (!res.ok) {
      const message = await res.text();
      await store.dispatch(flashErrorMessage({ message }));
    } else {
      const reportData = await res.json();
      await this.setComponentState({
        reportData,
      });
    }
    await this.setLoading(false);
  }
  renderTable = () => {
    const { barChart, headerColor, appConfig, t } = this.props;
    const { projectCurrencies } = this.state;
    const data = this.checkData(this.state.reportData);
    if (data && data.length > 0) {
      return (
        <Table
          basic={barChart && 'very'}
          celled={!barChart}
          structured={!barChart}
          color={!barChart ? headerColor : null}
          compact
          unstackable
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('Unit Status')}</Table.HeaderCell>
              {barChart && <Table.HeaderCell />}
              <Table.HeaderCell textAlign={barChart ? 'left' : 'right'} className={barChart ? 'hide-mobile-six-hundred-px' : null}>{t('Count')}</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">{t('Selling Price Sum')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {
              data.map((statusReport, idx) => (
                <Table.Row
                  key={idx}
                  style={statusReport.statusID === 'total' ? { fontWeight: 'bold' } : {}}
                >
                  <Table.Cell>
                    {this.getValueName(statusReport.statusID, UNIT_STATUSES_HQ_BASIC)}
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    {
                      barChart ? (
                        <CountUp
                          duration={2.1}
                          end={this.state.loading ? 0 : statusReport.count}
                          start={0}
                          useEasing
                        />
                      ) : statusReport.count
                    }
                  </Table.Cell>
                  {
                    barChart &&
                    <Table.Cell textAlign="left" className="hide-mobile-six-hundred-px" width={3}>
                      {
                        statusReport.statusID !== 'total' &&
                        <Progress
                          className={`progress-barchart ${statusReport.statusID}`}
                          percent={this.state.loading
                            ? 0
                            : this.getBarPercent(data, statusReport.count)
                          }
                        />
                      }
                    </Table.Cell>
                  }
                  <Table.Cell textAlign="right">
                    {
                      barChart && this.state.refetch ? (
                        <CountUp
                          decimals={2}
                          redraw
                          duration={2}
                          end={this.getConversionAmount(statusReport.sumSPAValue)}
                          prefix={this.setDisplayCurrencySymbol(appConfig)}
                          useEasing
                          useGrouping
                          separator=","
                        />
                      ) : (
                        <LoanAmount
                          projectCurrencies={projectCurrencies}
                          amount={statusReport.sumSPAValue}
                          format="0,000,000.00"
                          prefix={<CurrencySymbol />}
                        />
                      )
                    }
                  </Table.Cell>
                </Table.Row>
              ))
            }
          </Table.Body>
        </Table>
      );
    }
    return null;
  }
  render() {
    const { barChart, headerColor, t } = this.props;
    return (
      <Grid style={{ margin: '-1rem 0' }}>
        <Grid.Row style={barChart && { paddingTop: '0' }}>
          <Grid.Column>
            <Dimmer
              inverted style={{ zIndex: 20 }}
              active={this.state.loading}
            >
              <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
            </Dimmer>
            {
              barChart ? (
                <Segment color={headerColor}>
                  {this.renderTable()}
                </Segment>
              ) : this.renderTable()
            }
            {(this.props.auth.isHeadquarters || this.props.auth.isSuperAdmin) &&
              <OverallUnitReports
                location={this.props.location}
              />
            }
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation(namespaces)(connect(ProjectUnitsByStatus.mapStateToProps)(ProjectUnitsByStatus));



// WEBPACK FOOTER //
// ./src/components/Reports/ProjectUnitsByStatus.jsx